import { useApolloClient, useMutation } from "@apollo/client";
import { TrashIcon } from "@heroicons/react/outline";
import {
  FormEvent,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEvent,
  useRef,
  useState,
} from "react";
import {
  DeleteTeamMutationMutation,
  DeleteTeamMutationMutationVariables,
  GetOrganizationCareerTracksQuery,
  GetOrganizationCareerTracksQueryVariables,
  GetOrganizationCompetenciesQuery,
  GetOrganizationCompetenciesQueryVariables,
  UpdateTeamMutationMutation,
  UpdateTeamMutationMutationVariables,
} from "types/graphql-schema";

import getAllOrganizationTeamsQuery from "@apps/artifact/graphql/get-all-organization-teams-query";
import getLoggedInUserQuery from "@apps/main/graphql/get-logged-in-user-query";
import getOrganizationCareerTracksQuery from "@apps/org-settings/graphql/get-organization-career-tracks-query";
import getOrganizationCompetenciesQuery from "@apps/org-settings/graphql/get-organization-competencies-query";
import updateTeamMutation from "@apps/org-settings/graphql/update-team-mutation";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import GraphqlError from "@components/error/graphql-error";
import Input, { InputLabel } from "@components/input/input";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { UserComboboxOptionType } from "@components/user-combobox/user-combobox-constants";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import { assertEdgesNonNull, isCommandEnterEvent } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import deleteTeamMutation from "../graphql/delete-team-mutation";
import getOrganizationTeamsQuery from "../graphql/get-organization-teams-query";
import { getDepartmentDeletionSummary } from "./competencies/department-delete-summary";
import TeamMemberCombobox from "./team-member-combobox";

const emptyTeam = {
  id: null,
  title: "",
  members: { edges: [] },
  leads: [],
};

type TeamPerson = { id: number; name: string; avatar?: string | null };

// The people picked in one field of the dialog, each with a remove button.
const TeamPeopleList = ({
  people,
  removeLabel,
  onRemove,
}: {
  people: TeamPerson[];
  removeLabel: string;
  onRemove: (personId: number) => void;
}) => (
  <div className="flex flex-col text-sm text-gray-600 divide-y border-t border-b empty:hidden">
    {people.map((person) => (
      <div
        key={person.id}
        className="flex justify-between items-center gap-2 px-3 py-1.5 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Avatar user={person} size="4" />
          {person.name}
        </div>
        <button
          type="button"
          aria-label={removeLabel}
          onClick={() => onRemove(person.id)}
          className="rounded-sm hover:bg-gray-200 text-gray-500 hover:text-gray-800 px-1 py-0.5"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    ))}
  </div>
);

const TeamEditDialog = ({
  organization = null,
  team = null,
  onClose = null,
  onTeamSaved,
}: {
  organization: any;
  team?: any;
  onClose: any;
  onTeamSaved?: (team: { id: number; title: string }) => void;
}) => {
  const label = useLabel();
  const focusRef = useRef<HTMLElement>(null);
  const [formTeam, setFormTeam] = useState(team || emptyTeam);
  // Leads save the same way as members: only who was added or removed.
  // A lead need not be a member, and a department can have several.
  const [leads, setLeads] = useState<TeamPerson[]>(team?.leads ?? []);
  const [startingLeadIds] = useState<number[]>(() =>
    (team?.leads ?? []).map(({ id }: TeamPerson) => id),
  );
  // The members it opened with. Saving sends only who was added or removed
  // here: members it didn't load (past a page, or hidden) stay.
  const [startingMemberIds] = useState<number[]>(() =>
    (team || emptyTeam).members.edges.map(({ node }: { node: any }) => node.id),
  );
  const [updateTeam, { loading: isSaving }] = useMutation<
    UpdateTeamMutationMutation,
    UpdateTeamMutationMutationVariables
  >(updateTeamMutation);
  const [deleteTeam] = useMutation<
    DeleteTeamMutationMutation,
    DeleteTeamMutationMutationVariables
  >(deleteTeamMutation);

  const error = null;

  const handleSave = (
    e:
      | MouseEvent<HTMLButtonElement>
      | FormEvent<HTMLFormElement>
      | KeyboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const memberIds: number[] = formTeam.members.edges.map(
      ({ node }: { node: any }) => node.id,
    );
    const leadIds = leads.map(({ id }) => id);
    updateTeam({
      variables: {
        id: formTeam.id,
        title: formTeam.title,
        addMemberIds: memberIds.filter((id) => !startingMemberIds.includes(id)),
        removeMemberIds: startingMemberIds.filter(
          (id) => !memberIds.includes(id),
        ),
        addLeadUserIds: leadIds.filter((id) => !startingLeadIds.includes(id)),
        removeLeadUserIds: startingLeadIds.filter(
          (id) => !leadIds.includes(id),
        ),
        organizationId: organization.id,
      },
      // Team pickers list teams from getAllOrganizationTeamsQuery, and the
      // logged-in user's own departments come from getLoggedInUserQuery.
      refetchQueries: [
        getLoggedInUserQuery,
        getOrganizationTeamsQuery,
        getAllOrganizationTeamsQuery,
      ],
      // A lead who left between picking and saving. The dialog stays open,
      // so the admin can remove them and save again.
      context: {
        errorMatches: [
          {
            match: "A lead must be an active member",
            title: `Couldn't save the ${label("team")}`,
            description:
              "One of the leads is no longer an active member. Remove them, then save again.",
          },
        ],
      },
      onCompleted: (data) => {
        const savedTeam = data.createOrUpdateTeam?.team;
        if (savedTeam && onTeamSaved) {
          onTeamSaved(savedTeam);
        }
        onClose(false);
      },
      // The error link shows the toast. Passing onError also stops the call
      // rejecting with nobody to catch it.
      onError: () => {},
    });
  };

  const handleClose = () => {
    onClose(false);
  };

  const client = useApolloClient();
  const {
    ConfirmationDialog: DeleteConfirmationDialog,
    confirm: confirmDelete,
    setTitle: setDeleteTitle,
    setMessage: setDeleteMessage,
  } = useConfirm(`Delete ${label("team")}?`, "");

  // Deleting a department deletes its career tracks too, and narrows or
  // widens competencies scoped to it, so the warning reads what's saved now.
  const handleDelete = async () => {
    if (!team) {
      return;
    }
    let summary: ReturnType<typeof getDepartmentDeletionSummary>;
    try {
      const [tracksResult, competenciesResult] = await Promise.all([
        client.query<
          GetOrganizationCareerTracksQuery,
          GetOrganizationCareerTracksQueryVariables
        >({
          query: getOrganizationCareerTracksQuery,
          variables: {
            organizationId: organization.id,
            teamId: team.id,
            withHolders: true,
          },
          // `careerTracks` is cached by organization alone, so this one
          // department's tracks would replace every page's full list.
          fetchPolicy: "no-cache",
        }),
        client.query<
          GetOrganizationCompetenciesQuery,
          GetOrganizationCompetenciesQueryVariables
        >({
          query: getOrganizationCompetenciesQuery,
          variables: { organizationId: organization.id },
          fetchPolicy: "network-only",
        }),
      ]);
      summary = getDepartmentDeletionSummary({
        departmentId: team.id,
        departmentTitle: team.title,
        memberCount:
          team.members?.totalCount ?? team.members?.edges.length ?? 0,
        tracks: assertEdgesNonNull(tracksResult.data.careerTracks).map(
          (track) => ({
            id: track.id,
            title: track.title,
            roles: assertEdgesNonNull(track.roles).map((role) => ({
              assignedUsers: assertEdgesNonNull(role.assignedUsers),
            })),
          }),
        ),
        competencies: assertEdgesNonNull(
          competenciesResult.data.competencies,
        ).map((competency) => ({
          ...competency,
          teamIds: assertEdgesNonNull(competency.appliesToTeams).map(
            ({ id }) => id,
          ),
          careerTrackIds: assertEdgesNonNull(
            competency.appliesToCareerTracks,
          ).map(({ id }) => id),
        })),
        label,
      });
    } catch {
      errorNotificationVar({
        title: `Couldn't check what deleting this ${label("team")} affects. Nothing was deleted.`,
      });
      return;
    }
    const { title, deleted, changed, kept } = summary;
    setDeleteTitle(title);
    setDeleteMessage(
      <div className="flex flex-col gap-2">
        <p>
          This deletes the {team.title} {label("team")}
          {deleted.length > 0 ? ", along with:" : "."}
        </p>
        {deleted.length > 0 && (
          <ul className="list-disc pl-5 flex flex-col gap-1">
            {deleted.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {changed.length > 0 && (
          <>
            <p>
              {changed.length === 1
                ? `1 ${label("competency")} changes who it applies to:`
                : `${changed.length} ${label("competency", {
                    pluralize: true,
                  })} change who they apply to:`}
            </p>
            <ul className="list-disc pl-5 flex flex-col gap-1">
              {changed.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </>
        )}
        {kept && <p>{kept}</p>}
        <p>This can&apos;t be undone.</p>
      </div>,
    );
    if (!(await confirmDelete())) {
      return;
    }
    deleteTeam({
      variables: { id: team.id },
      // Its tracks go with it, so every list of tracks and departments.
      refetchQueries: [
        getOrganizationTeamsQuery,
        getAllOrganizationTeamsQuery,
        getOrganizationCareerTracksQuery,
      ],
      onCompleted: handleClose,
    });
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (isCommandEnterEvent(e)) {
      handleSave(e);
    }
  };

  const handleAddMember = (member: any) => {
    const newMemberEdges = [...formTeam.members.edges, { node: member }];
    setFormTeam({ ...formTeam, members: { edges: newMemberEdges } });
  };

  const handleRemoveMember = (memberId: number) => {
    const newMemberEdges = formTeam.members.edges.filter(
      ({ node }: { node: any }) => node.id !== memberId,
    );
    setFormTeam({ ...formTeam, members: { edges: newMemberEdges } });
  };

  const handleAddLead = (option: UserComboboxOption | null) => {
    if (option?.type === UserComboboxOptionType.USER) {
      setLeads([...leads, option]);
    }
  };

  return (
    <>
      <Modal
        open
        // It grows as people are picked: anchored, it doesn't move.
        alignedTop
        onClose={onClose}
        initialFocus={focusRef}
        aria-label="Team edit dialog"
      >
        {/* Inside the modal, so Headless UI treats it as nested: cancelling
            it returns to this dialog rather than closing both. */}
        <DeleteConfirmationDialog />
        <div className="p-6 bg-white rounded-md">
          <div className="mb-4">
            <ModalTitle onClose={handleClose}>
              {team
                ? `Edit ${formTeam.title}`
                : `New ${label("team", { capitalize: true })}`}
            </ModalTitle>
          </div>
          <form className="mt-4 flex flex-col gap-6" onSubmit={handleSave}>
            <div className=" max-w-96">
              <Input
                label={`${label("team", { capitalize: true })} name`}
                aria-label="Team title input"
                value={formTeam.title}
                onKeyDown={handleKeyDown}
                onChange={(e) =>
                  setFormTeam({ ...formTeam, title: e.target.value })
                }
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="max-w-96">
                <InputLabel
                  label={`${label("team", { capitalize: true })} members`}
                  className="mb-1"
                />
                <TeamMemberCombobox
                  // The department's organization, which may not be the one selected.
                  organizationId={organization.id}
                  excludeUserIds={formTeam.members.edges.map(
                    ({ node }: { node: any }) => node.id,
                  )}
                  onSelect={handleAddMember}
                />
              </div>
              <TeamPeopleList
                people={formTeam.members.edges.map(
                  ({ node }: { node: any }) => node,
                )}
                removeLabel="Delete team member button"
                onRemove={handleRemoveMember}
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="max-w-96">
                <InputLabel
                  label={`${label("team", { capitalize: true })} leads`}
                  className="mb-1"
                />
                <p className="text-xs text-gray-500 mb-2">
                  Leads edit this {label("team")}&apos;s career tracks. They
                  don&apos;t need to be members.
                </p>
                <TeamMemberCombobox
                  organizationId={organization.id}
                  canLead
                  placeholder="Pick a lead"
                  aria-label={`${label("team", { capitalize: true })} lead combobox`}
                  excludeUserIds={leads.map(({ id }) => id)}
                  onSelect={handleAddLead}
                />
              </div>
              <TeamPeopleList
                people={leads}
                removeLabel={`Remove ${label("team")} lead button`}
                onRemove={(leadId) =>
                  setLeads(leads.filter(({ id }) => id !== leadId))
                }
              />
            </div>
            {error && (
              <div>
                <GraphqlError error={error} />
              </div>
            )}
            {/* Laid out like the career track details modal: Delete on the
                left, Cancel and Save on the right. A new department has
                nothing to delete yet. */}
            <div className="flex items-center gap-2 mt-4">
              {team && (
                <Button
                  theme={buttonTheme.redDanger}
                  onClick={handleDelete}
                  text="Delete"
                />
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button
                  theme={buttonTheme.text}
                  onClick={handleClose}
                  text="Cancel"
                />
                <Button
                  type="submit"
                  theme={buttonTheme.primary}
                  onClick={handleSave}
                  disabled={formTeam.title.trim().length === 0 || isSaving}
                  text="Save"
                />
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default TeamEditDialog;
